// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartDetails.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartDetails model
 */

var m = require('mobx');
var ReportPartElement = require('./Izenda.ReportPartElement');
var ReportPartGridProperties = require('./Izenda.ReportPartGridProperties');
var ReportPartGridConstants = require('logic/data/Izenda.ReportPartGridConstants');
var GridDefaultConfig = require('logic/data/Izenda.GridDefaultConfig');
var trackable = require('share/tracking/Izenda.Trackable');
var ReportUtils = require('./Izenda.ReportUtils');
var ReportDesignerConstants = require('logic/data/Izenda.ReportDesignerConstants');
var ReportPartFieldConstants = require('logic/data/Izenda.ReportPartFieldConstants');
var availableFunction = require('logic/data/Izenda.GridAvailableFunction');
var reportPartUtils = require('share/utils/Izenda.ReportPartUtils');
var ReportBodyConstants = require('logic/data/Izenda.ReportBodyConstants');
var ObjectUtils = require('share/utils/Izenda.ObjectUtils');
var SORT_ORDERS = ReportPartFieldConstants.SORT_ORDERS;
var FUNCTION = require('logic/data/Izenda.ReportPartFieldConstants').FUNCTION;

const {
  filter,
  isEmpty,
  some,
  forEach,
  map,
  isNil,
  flow,
  getOr,
  omit,
  get,
  assign,
  compact,
  flatMap,
  defaults,
  includes,
  invoke,
  each,
  clone,
  max,
  find
} = require('lodash/fp');

/**
 * create a container from data from api
 * @param  {Object} data [data from api]
 * @return {container}   [container with elements typeof ReportPartElement]
 */
function populateReportPartContainer(name, data) {
  if (!data) {
    return {
      elements: [],
      name: name,
      visibleElement: ReportUtils.getVisibleField
    };
  }
  var pickData = omit('elements', data);
  var elements = data.elements;
  /* convert elements json object to become list of ReportPartElement*/
  var convertElements = map(function(item) {
    return new ReportPartElement().readData(item);
  }, elements);

  return m.observable(
    assign(pickData, {
      elements: convertElements || [],
      name: name,
      visibleElement: ReportUtils.getVisibleField
    })
  );
}

function ReportPartGridContent(data) {
  this.subReportDefinitionCollection = [];
  this.collapseStatus = 0;
  this.autoSize = true;

  var props = {
    isCrossFiltering: false,
    type: ReportDesignerConstants.REPORT_PART_TYPE.Grid,
    columns: populateReportPartContainer('columns'),
    rows: populateReportPartContainer('rows'),
    values: populateReportPartContainer('values'),
    separators: populateReportPartContainer('separators'),
    properties: new ReportPartGridProperties(),
    settings: {},
    changeGridStyleStatus: true,
    containerCollection: function() {
      return compact([this.columns, this.rows, this.values, this.separators]);
    },
    gridStyle: function() {
      return this.properties.generalInfo.gridStyle;
    },
    optionalParams: function() {
      var sortOrders = map(function(field) {
        var sortValue = get('properties.dataFormattings.sort', field);

        return { key: field.field.fieldNameAlias, sortingType: sortValue || SORT_ORDERS.Unsorted.value };
      }, this.fieldCollection);

      var params = { expandedLevel: ReportDesignerConstants.EXPANDED_LEVEL.ALL, sortOrders: sortOrders };

      if (this.properties.view.usePagination) {
        params = defaults(params, {
          paging: {
            pageIndex: ReportDesignerConstants.DEFAULT_PAGE_INDEX,
            pageSize: this.properties.view.pageSize || ReportDesignerConstants.DEFAULT_PAGE_SIZE
          }
        });
      }

      return params;
    },
    isBeingBuild: function() {
      if (this.inconsitentFunction) {
        return false;
      }
      switch (this.gridStyle) {
        case ReportPartGridConstants.GRID_STYLES.VERTICAL.value:
          return !isEmpty(this.columns.visibleElement);
        case ReportPartGridConstants.GRID_STYLES.HORIZONTAL.value:
          return !isEmpty(this.rows.visibleElement);
        case ReportPartGridConstants.GRID_STYLES.DRILL_DOWN.value:
          return !isEmpty(this.rows.visibleElement) && !isEmpty(this.values.visibleElement);
        default:
          return (
            !isEmpty(this.columns.visibleElement) &&
            !isEmpty(this.rows.visibleElement) &&
            !isEmpty(this.values.visibleElement)
          );
      }
    },
    haveRowExpander: function() {
      switch (this.gridStyle) {
        case ReportPartGridConstants.GRID_STYLES.DRILL_DOWN.value:
          return true;
        case ReportPartGridConstants.GRID_STYLES.PIVOT.value:
          return get('properties.useExpanders', this.rows);
        default:
          return false;
      }
    },
    useSeparator: function() {
      return this.properties.grouping.useSeparator && !isEmpty(this.separators.elements);
    },
    containersNeedDefaultFunction: function() {
      switch (this.gridStyle) {
        case ReportPartGridConstants.GRID_STYLES.PIVOT.value:
          return [this.columns, this.values, this.rows, this.separators];
        case ReportPartGridConstants.GRID_STYLES.DRILL_DOWN.value:
          return [this.values, this.rows, this.separators];
        default:
          return [];
      }
    },
    fieldCollection: function() {
      return flatMap(function(item) {
        return item.elements ? item.elements.slice() : [];
      }, this.containerCollection);
    },
    haveFunction: function() {
      return some('haveFunction', this.fieldCollection);
    },
    separatorStyle: function() {
      return find({ value: this.properties.generalInfo.separatorStyle }, ReportPartGridConstants.SEPARATOR_STYLE);
    },
    inconsitentFunction: false,
    usePageBreakAfterSeparator: function() {
      return this.properties.printing.usePageBreakAfterSeparator;
    },
    dataRefreshInterval: function() {
      return get('view.dataRefreshInterval', this.properties);
    },
    reportPartStyle: function() {
      return this.properties.generalInfo.gridStyle;
    }
  };

  m.extendObservable(trackable(this), props);

  data && this.readData(data);

  this.cleanDirty();
}

ReportPartGridContent.prototype.checkHiddenSortField = function(field, container) {
  return (
    includes(this.gridStyle, [
      ReportPartGridConstants.GRID_STYLES.PIVOT.value,
      ReportPartGridConstants.GRID_STYLES.DRILL_DOWN.value
    ]) && container.name === ReportPartGridConstants.CONTAINER_NAME.VALUES
  );
};

ReportPartGridContent.prototype.validateCanAddField = function(reportDesigner, container, fieldCollection) {
  var isDistinct = get('dataSource.distinct', reportDesigner);
  var gridStyle = this.properties.generalInfo.gridStyle;
  var containerName = container.name;
  var messageTemplate = null;
  var warning = true;

  if (isDistinct) {
    messageTemplate = ReportBodyConstants.MESSAGES.M05_1;
  } else {
    switch (gridStyle) {
      case ReportPartGridConstants.GRID_STYLES.VERTICAL.value: // VERTICAL
      case ReportPartGridConstants.GRID_STYLES.HORIZONTAL.value: // HORIZONTAL
        if (containerName === ReportPartGridConstants.CONTAINER_NAME.SEPARATORS) {
          warning = false;
          messageTemplate = ReportBodyConstants.MESSAGES.M08;
        } else if (this.haveFunction) {
          messageTemplate = ReportBodyConstants.MESSAGES.M08;
        }
        break;
      case ReportPartGridConstants.GRID_STYLES.DRILL_DOWN.value: // DRILL_DOWN
      case ReportPartGridConstants.GRID_STYLES.PIVOT.value: // PIVOT
        warning = false;
        messageTemplate =
          containerName === ReportPartGridConstants.CONTAINER_NAME.VALUES
            ? ReportBodyConstants.MESSAGES.M09
            : ReportBodyConstants.MESSAGES.M08;
        break;
    }
  }

  var errorField = filter(function(field) {
    return ObjectUtils.isBinary(field.izendaDataType || field.fieldDataType);
  }, fieldCollection);

  if (messageTemplate && !isEmpty(errorField)) {
    return {
      message: String.format(messageTemplate, map('name', errorField).join(', ')),
      errorField: !warning ? errorField : []
    };
  }

  return {
    message: null,
    errorField: []
  };
};
/**
 * check if function is compatible with element
 * @method checkedCompatibleFunction
 * @param  {Object}                  container    Container that element belong
 * @param  {Object}                  element      Element need to validate
 * @param  {Object}                  functionInfo Function infomation
 * @return {Boolean}                              Result
 */
ReportPartGridContent.prototype.checkedCompatibleFunction = function(container, element, functionInfo) {
  var result = true;

  if (element.field.hasAggregatedFunction) {
    result = reportPartUtils.checkedCompatibleFunctionHasAggregatedFunction(functionInfo);
  }

  var config = availableFunction.AVAILABLE_FUNCTION_CONFIG[this.gridStyle];

  config = config && container && config[container.name];

  if (!config || !result) {
    return result;
  }

  return reportPartUtils.checkCompatibleFunctionCommon(functionInfo, element, config);
};

/**
 * read data from api/create report part to update data
 * @param {Object} content [data from api/create report part]
 */
ReportPartGridContent.prototype.readData = m.action(function(content) {
  content = content || {};
  this.type = content.type;
  this.isCrossFiltering = content.isCrossFiltering;
  this.columns = populateReportPartContainer('columns', content.columns);
  this.rows = populateReportPartContainer('rows', content.rows);
  this.values = populateReportPartContainer('values', content.values);
  this.separators = populateReportPartContainer('separators', content.separators);
  content.properties && this.properties.setReportPartProperties(content.properties);
  this.settings = content.settings;
  this.dataSource = content.dataSource || {};
  this.markDirty();
});

/**
 * validate function based on container
 * @method validateFunction
 * @param  {object}         container    container need validateFunction
 * @param  {object}         functionInfo Function need validate
 * @return {json}                      validate result
 */
ReportPartGridContent.prototype.validateFunction = function(container, functionInfo) {
  if (!functionInfo && includes(container, this.containersNeedDefaultFunction)) {
    return {
      status: false,
      useDefaultFunction: true
    };
  }

  return {
    status: true
  };
};

/**
 * [getDefaultFunctionFormat Get Default Function And Format for field in container]
 * @method getDefaultFunctionFormat
 * @param  {json}                 container Container have element need get default dataType
 * @param  {string}                 dataType  Data Type for field
 * @return {json}                           default data
 */
ReportPartGridContent.prototype.getDefaultFunctionFormat = function(container, dataType) {
  var fieldContainerName = container.name;
  var gridStyleDefault = GridDefaultConfig.DefaultConfig[this.gridStyle];
  var functionDefine = this.haveFunction
    ? gridStyleDefault.DEFAULT_FUNCTION_WITH_AREGATED_FUNCTION
    : gridStyleDefault.DEFAULT_FUNCTION;
  var functionContainer = functionDefine[fieldContainerName] || {};

  return functionContainer[dataType];
};

/**
 * action invoke after insert new field into container
 * @method afterInsertFieldToContainer
 * @param  {json}                      container        Container with new field
 * @param  {ReportPartElement}         field            Field just created
 * @return {promise}
 */
ReportPartGridContent.prototype.afterInsertFieldToContainer = function(container, field) {
  if (field.haveFunction) {
    return reportPartUtils.convertNonFunctionElementsToFunction(this, ReportPartFieldConstants.FUNCTION.GROUP.NAME);
  }
  return Promise.resolve();
};

/**
 * after update function for field
 * @method afterUpdateFunction
 * @param  {ReportPartElement}            field            Field just update function
 * @return {Promise}                                       return promise
 */
ReportPartGridContent.prototype.afterUpdateFunction = m.action(function(field) {
  var checkInconsistentFunction = function() {
    this.inconsitentFunction =
      some({ haveFunction: true }, this.fieldCollection) && some({ haveFunction: false }, this.fieldCollection);
  }.bind(this);
  if (field.haveFunction) {
    var functionName = get('functionInfo.name', field);
    return (
      functionName !== FUNCTION.DAYS_OLD.NAME &&
      reportPartUtils
        .convertNonFunctionElementsToFunction(this, ReportPartFieldConstants.FUNCTION.GROUP.NAME)
        .then(checkInconsistentFunction)
    );
  }
  return Promise.resolve().then(checkInconsistentFunction);
});

/**
 * Return data object that represents for a report part.
 */
ReportPartGridContent.prototype.getData = function() {
  return omit('dataSource', m.toJS(this));
};

/**
 * Get dirty status.
 */
ReportPartGridContent.prototype.checkDirty = function() {
  var dirtyElements = ReportUtils.dirtyReportPartElementField(this.containerCollection);
  var properties = this.properties;

  return this.isDirty || !isEmpty(dirtyElements) || (properties && properties.checkDirty && properties.checkDirty());
};

/**
 * Clean dirty status.
 */
ReportPartGridContent.prototype.cleanDirty = function() {
  var dirtyElements = ReportUtils.dirtyReportPartElementField(this.containerCollection);

  each(function(item) {
    item.resetDirty && item.resetDirty();
  }, dirtyElements);

  var properties = this.properties;

  invoke('resetDirty', properties);

  this.resetDirty();
};

ReportPartGridContent.prototype.changeGridStyle = m.action(function(oldStyle, newStyle, reportPartDetail) {
  forEach(function(item) {
    item.properties = null;
  }, this.containerCollection);

  forEach(
    function(item) {
      this[item.key].properties = m.observable(clone(item.properties));
    }.bind(this),
    ReportPartGridConstants.GRID_STYLES_CONSTANT_PROPERTIES[newStyle]
  );

  this.properties.generalInfo.gridStyle = newStyle;

  var mappings = find(
    {
      fromStyle: oldStyle,
      toStyle: newStyle
    },
    ReportPartGridConstants.MAPPING_GRID_STYLE
  );

  if (!mappings) {
    return Promise.resolve();
  }

  /**
   * each mapping map from mulltiple container to 1 container
   * @type {[type]}
   */
  forEach(
    function(mapping) {
      var toContainer = get(mapping.toContainer, this);
      var newArray = [];

      /**
       * move each element to new container
       * @method moveToContainer
       * @param  {[type]}        element [description]
       * @return {[type]}                [description]
       */
      var moveToContainer = function(element) {
        element.position =
          (flow(
            map('position'),
            max
          )(newArray) || 0) + 1;
        newArray.push(element);
      };

      forEach(
        function(item) {
          var fromContainer = get(item, this);
          var elements = fromContainer.elements;

          forEach(moveToContainer, elements);
          fromContainer.elements = [];
        }.bind(this),
        mapping.fromContainer
      );

      toContainer.elements = newArray;
    }.bind(this),
    mappings.mappings
  );

  var promiseArray = flow(
    flatMap(
      function(container) {
        return map(
          function(element) {
            var needUpdateFunction = !this.checkedCompatibleFunction(
              container,
              element,
              getOr({}, 'properties.dataFormattings.functionInfo', element)
            );

            /**
             * update function if the current function not compatible
             */
            if (needUpdateFunction) {
              return reportPartUtils.setDefaultFunctionElement(this, container, element);
            }

            return null;
          }.bind(this),
          container.elements
        );
      }.bind(this)
    ),
    compact
  )(this.containerCollection);

  this.changeGridStyleStatus = isEmpty(promiseArray);
  return Promise.all(promiseArray);
});

ReportPartGridContent.prototype.updateColumnsUseExpanders = m.action(function(value) {
  this.columns.properties.useExpanders = value;
  this.markDirty();
});

ReportPartGridContent.prototype.changePageSize = m.action(function(pageSize) {
  this.properties.changePageSize(pageSize);
  this.markDirty();
});

ReportPartGridContent.prototype.updateColumnsAddSideTotal = m.action(function(value) {
  this.columns.properties.addSideTotal = value;
  this.markDirty();
});

ReportPartGridContent.prototype.updateRowsUseExpanders = m.action(function(value) {
  this.rows.properties.useExpanders = value;
  this.markDirty();
});

/**
 * Check need to break page when printing.
 */
ReportPartGridContent.prototype.checkBreakPage = function() {
  return this.useSeparator && this.usePageBreakAfterSeparator;
};

ReportPartGridContent.prototype.getNewSortValue = function(container, oldValue, configField) {
  switch (oldValue) {
    case SORT_ORDERS.ASC.value:
      return SORT_ORDERS.DESC.value;
    case SORT_ORDERS.DESC.value:
      return this.checkCanUnsort(container, configField) ? '' : SORT_ORDERS.ASC.value;
    default:
      return SORT_ORDERS.ASC.value;
  }
};

/**
 * Reset sort values
 * @param {Object} container
 */
ReportPartGridContent.prototype.resetSortValue = m.action(function(container: Object) {
  forEach(element => element.properties.setSortValue(SORT_ORDERS.Unsorted.value), container.elements);
});

/**
 * Check if this field can have state Unsort
 * @param {type} reportDesigner
 * @param {type} container
 */
ReportPartGridContent.prototype.checkCanUnsort = function(container, configField) {
  return reportPartUtils.checkCanUnsort(this.gridStyle, container, configField, this.containerCollection);
};

/**
 * Get drill info.
 */
ReportPartGridContent.prototype.getDrilldownInfo = function(expandByDefault, defaultValue) {
  var hasSubTotal = some('hasSubTotal', this.values.visibleElement);
  var expandedLevel;

  if (expandByDefault || hasSubTotal) {
    expandedLevel = -1;
  } else if (!isNil(defaultValue)) {
    expandedLevel = defaultValue;
  } else {
    expandedLevel = this.properties.view.collapseDrilldownByDefault ? 0 : -1;
  }
  if (expandedLevel >= 0) {
    let firstVisibleLevel = Math.min(...this.rows.visibleElement.map(e => e.position)) - 1;
    expandedLevel = Math.max(expandedLevel, firstVisibleLevel);
  }
  return {
    expandedLevel: expandedLevel,
    filters: []
  };
};

ReportPartGridContent.prototype.setCollapseStatus = function(status) {
  this.collapseStatus = status;
};

module.exports = ReportPartGridContent;



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartGridContent.js